<template>
  <div class="w-full flex flex-col h-full">
    <!-- Main content -->
    <div class="w-full">
      <Tabs
        v-model:value="activeTab"
        class="w-full"
      >
        <TabList class="w-full flex border-b">
          <Tab value="overview">
            Overview
          </Tab>
          <Tab value="address">
            Address
          </Tab>
          <Tab value="billing-address">
            Billing Address
          </Tab>
          <Tab value="staff">
            Staff
          </Tab>
        </TabList>
        <TabPanels class="w-full py-6">
          <TabPanel
            value="overview"
            class="w-full"
          >
            <CompanyOverviewTab
              :company="company"
              :is-editing="isEditing"
              @update:company="updateCompany"
            />
          </TabPanel>
          <TabPanel
            value="address"
            class="w-full"
          >
            <CompanyAddressTab
              :company="company"
              :is-editing="isEditing"
              @update:company="updateCompany"
            />
          </TabPanel>
          <TabPanel
            value="billing-address"
            class="w-full"
          >
            <CompanyBillingAddressTab
              :company="company"
              :is-editing="isEditing"
              @update:company="updateCompany"
            />
          </TabPanel>
          <TabPanel
            value="staff"
            class="w-full"
          >
            <CompanyStaffTab
              :company="company"
              :is-editing="isEditing"
              @update:company="updateCompany"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <!-- Footer with action buttons -->
    <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
      <div
        v-if="type === 'view' && !isEditing"
        class="flex gap-2"
      >
        <Button
          label="Edit"
          @click="startEdit"
        />
        <Button
          label="Delete"
          class="p-button-danger"
          @click="triggerDelete"
        />
      </div>

      <div
        v-if="isEditing"
        class="flex gap-2"
      >
        <Button
          v-if="type === 'add'"
          label="Save"
          :disabled="isSaving"
          :loading="isSaving"
          @click="saveChanges"
        />
        <Button
          v-if="type === 'view'"
          label="Save"
          :disabled="isSaving"
          :loading="isSaving"
          @click="updateItem"
        />
        <Button
          label="Cancel"
          class="p-button-secondary"
          :disabled="isSaving"
          @click="cancelEdit"
        />
      </div>
    </div>
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, provide } from "vue";
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useCompanyStore } from '@/stores/companyStore';
import { uploadCompanyImage } from '@/services/companyService';
import { mapCompanyToBackend, mapCompanyFromBackend } from '@/utils/companyFieldMapper';
import CompanyOverviewTab from '@/components/contacts/view/CompanyOverviewTab.vue';
import CompanyAddressTab from '@/components/contacts/view/CompanyAddressTab.vue';
import CompanyBillingAddressTab from '@/components/contacts/view/CompanyBillingAddressTab.vue';
import CompanyStaffTab from '@/components/contacts/view/CompanyStaffTab.vue';

const props = defineProps({
    data: { type: Object, default: () => ({}) },
    type: { type: String, default: 'view' },
});

const emit = defineEmits(['company-created', 'add']);
const router = useRouter();
const companyStore = useCompanyStore();
const toast = useToast();
const confirm = useConfirm();

const company = ref({ ...props.data });
const isEditing = ref(false);
const isSaving = ref(false);
const imageUploadFile = ref(null);
const activeTab = ref('overview');

// Form actions
const startEdit = () => {
    isEditing.value = true;
};

const cancelEdit = () => {
    isEditing.value = false;
    // Reset company data to original props
    if (props.type === 'add') {
        company.value = {
            company_name: "",
            official_name: "",
            registration_number: "",
            tax_number: "",
            vat_number: "",
            website: "",
            phone_number: "",
            contact_email: "",
            accounts_email: "",
            description: "",
            company_status: "active",
            discount: 0,
            logo_path: null,
            logo_url: null,
            image_url: null,
            image_path: null,
            tags: [],
            staff: [],
            address: {
                address_1: "",
                address_2: "",
                city: "",
                state: "",
                postcode: "",
                country: ""
            },
            billing_address: null
        };
        imageUploadFile.value = null;
    } else {
        company.value = { ...props.data };
    }
};

const updateCompany = (updatedCompany) => {
    // Properly merge nested objects (address, billing_address)
    if (updatedCompany.address) {
        company.value.address = { ...(company.value.address || {}), ...updatedCompany.address };
    }
    if (updatedCompany.billing_address) {
        company.value.billing_address = { ...(company.value.billing_address || {}), ...updatedCompany.billing_address };
    }
    // Merge other fields
    Object.assign(company.value, updatedCompany);
};

const saveChanges = async () => {
    if (isSaving.value) return;
    
    if (!company.value.company_name || company.value.company_name.trim() === '') {
        toast.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Company name is required',
            life: 5000
        });
        return;
    }

    isSaving.value = true;
    try {
        // Map frontend format to backend format
        const backendData = mapCompanyToBackend(company.value);
        
        // Create company
        const response = await companyStore.createCompany(backendData);
        // Backend returns: { success: true, message: "...", data: { id, ... } }
        const newCompanyId = response.data?.id || response.id;
        
        if (!newCompanyId) {
            throw new Error('Company created but no ID returned');
        }
        
        // Upload logo if one was selected
        if (imageUploadFile.value) {
            try {
                await uploadCompanyImage(newCompanyId, imageUploadFile.value);
            } catch (imageError) {
                console.error('Error uploading logo:', imageError);
                toast.add({
                    severity: 'warn',
                    summary: 'Logo Upload Failed',
                    detail: 'Company created but logo upload failed. You can upload it later.',
                    life: 7000
                });
            }
        }
        
        toast.add({
            severity: 'success',
            summary: 'Company Created',
            detail: 'Company created successfully!',
            life: 5000
        });
        
        // Emit event to parent to close dialog
        emit('company-created', response);
        emit('add', response);
    } catch (error) {
        console.error('Error creating company:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to create company');
        toast.add({
            severity: 'error',
            summary: 'Creation Failed',
            detail: errorMessage,
            life: 7000
        });
    } finally {
        isSaving.value = false;
    }
};

const updateItem = async () => {
    if (isSaving.value) return;
    
    if (!company.value.company_name || company.value.company_name.trim() === '') {
        toast.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Company name is required',
            life: 5000
        });
        return;
    }

    isSaving.value = true;
    try {
        // Map frontend format to backend format
        const backendData = mapCompanyToBackend(company.value);
        
        // Update company
        const response = await companyStore.updateCompany({
            id: company.value.id,
            ...backendData
        });
        
        // Upload logo if one was selected
        if (imageUploadFile.value) {
            try {
                const imageResponse = await uploadCompanyImage(company.value.id, imageUploadFile.value);
                // Backend returns: { success: true, message: "...", data: { image_url, logo_path } }
                // Update company with new image URLs
                company.value.image_url = imageResponse.data?.image_url || company.value.image_url;
                company.value.logo_url = imageResponse.data?.image_url || imageResponse.data?.logo_url || company.value.logo_url;
                company.value.logo_path = imageResponse.data?.logo_path || company.value.logo_path;
                company.value.image_path = imageResponse.data?.image_path || imageResponse.data?.logo_path || company.value.image_path;
            } catch (imageError) {
                console.error('Error uploading logo:', imageError);
                toast.add({
                    severity: 'warn',
                    summary: 'Logo Upload Failed',
                    detail: 'Company updated but logo upload failed. You can try again later.',
                    life: 7000
                });
            }
        } else {
            // Map response back to frontend format
            company.value = mapCompanyFromBackend(response);
        }
        
        toast.add({
            severity: 'success',
            summary: 'Company Updated',
            detail: 'Company updated successfully!',
            life: 5000
        });
        
        isEditing.value = false;
        imageUploadFile.value = null;
    } catch (error) {
        console.error('Error updating company:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to update company');
        toast.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: errorMessage,
            life: 7000
        });
    } finally {
        isSaving.value = false;
    }
};

const triggerDelete = () => {
    confirm.require({
        message: 'Are you sure you want to delete this company? This action cannot be undone.',
        header: 'Confirm Delete',
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        rejectLabel: 'Cancel',
        acceptLabel: 'Delete',
        accept: async () => {
            try {
                await companyStore.deleteCompany(company.value.id);
                toast.add({
                    severity: 'success',
                    summary: 'Company Deleted',
                    detail: 'Company deleted successfully',
                    life: 5000
                });
                router.push('/companies');
            } catch (error) {
                console.error('Error deleting company:', error);
                const errorMessage = error.response?.data?.message || 'Failed to delete company';
                toast.add({
                    severity: 'error',
                    summary: 'Delete Failed',
                    detail: errorMessage,
                    life: 7000
                });
            }
        }
    });
};

// Handle image upload from child components (for add mode)
const handleImageUpload = (file) => {
    imageUploadFile.value = file;
    
    // Preview image immediately
    const reader = new FileReader();
    reader.onload = () => {
        company.value.logo_path = reader.result;
        company.value.logo_url = reader.result;
    };
    reader.readAsDataURL(file);
};

// Expose image upload handler to child components via provide/inject
provide('handleImageUpload', handleImageUpload);
provide('imageUploadFile', imageUploadFile);

// Initialize form on mount
onMounted(() => {
    isEditing.value = props.type === 'add';

    if (props.type === 'add') {
        // Initialize with empty data for a new company
        company.value = {
            company_name: "",
            official_name: "",
            registration_number: "",
            tax_number: "",
            vat_number: "",
            website: "",
            phone_number: "",
            contact_email: "",
            accounts_email: "",
            description: "",
            company_status: "active",
            discount: 0,
            tags: [],
            logo_path: null,
            logo_url: null,
            image_url: null,
            image_path: null,
            staff: [],
            address: {
                address_1: "",
                address_2: "",
                city: "",
                state: "",
                postcode: "",
                country: ""
            },
            billing_address: null
        };
    } else if (props.type === 'view' && props.data && props.data.id) {
        // Map backend format to frontend format if data is provided
        company.value = mapCompanyFromBackend(props.data);
    }
});

// Watch for changes in the type prop to adjust the form state
watch(
    () => props.type,
    (newType) => {
        isEditing.value = newType === "add";
    }
);
</script>

<style scoped>
/* Ensure components take full width */
:deep(.p-tabview-panels) {
    width: 100% !important;
}

:deep(.p-tabview-panel) {
    width: 100% !important;
}

:deep(.p-datatable) {
    width: 100% !important;
}
</style>
